import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Safe date formatter — returns fallback if date is null/undefined/invalid
const safeFormat = (dateVal, fmt, fallback = '—') => {
  if (!dateVal) return fallback;
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return fallback;
    return format(d, fmt);
  } catch { return fallback; }
};
import {
  ArrowLeft, Package, Truck, CheckCircle, Clock, Loader2,
  AlertTriangle, Camera, MessageSquare, ChevronRight, Shirt,
  Plus, Zap, Tag, Clock3, Star, CreditCard, XCircle, Receipt, IndianRupee,
  ScanSearch, Cog, Sparkles
} from 'lucide-react';
import IndividualBottomNav from '@/components/IndividualBottomNav';
import IndividualSidebar from '@/components/IndividualSidebar';

// ── Rating Modal ───────────────────────────────────────────────────────────────
const StarSelector = ({ value, onChange, label }) => (
  <div className="space-y-1.5">
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{label}</p>
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110 active:scale-95"
        >
          <Star
            className={`w-8 h-8 ${n <= value ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-100'}`}
            strokeWidth={1.5}
          />
        </button>
      ))}
      {value > 0 && (
        <span className="text-sm font-bold text-amber-600 ml-1">{value}/5</span>
      )}
    </div>
  </div>
);

const RatingModal = ({ order, token, onClose, onDone }) => {
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async () => {
    if (deliveryRating === 0 || satisfactionRating === 0) {
      toast.error('Please rate both delivery and satisfaction');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/individual/orders/${order.id}/rate`,
        { delivery_rating: deliveryRating, satisfaction_rating: satisfactionRating, comment: comment || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thank you for your feedback!');
      onDone(order.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Rate Your Order
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
            <p className="text-xs font-black text-emerald-700 uppercase tracking-widest mb-0.5">Delivered ✓</p>
            <p className="text-sm font-bold text-slate-800">
              {order.total_clothes != null ? `${order.total_clothes} items` : order.service_summary || 'Laundry order'}
            </p>
            <p className="text-xs text-slate-400">
              Ordered {safeFormat(order.order_date, 'dd MMM yyyy')}
              {order.pickup_time_slot ? ` · Pickup ${order.pickup_time_slot}` : ''}
            </p>
          </div>
          <p className="text-xs text-slate-400">How did we do? Your feedback helps us improve.</p>
          <StarSelector value={deliveryRating} onChange={setDeliveryRating} label="Delivery Experience" />
          <StarSelector value={satisfactionRating} onChange={setSatisfactionRating} label="Order Satisfaction" />
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Comment (optional)</label>
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Any additional feedback..."
              className="min-h-[70px] rounded-xl border-slate-200 bg-slate-50 text-sm"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || deliveryRating === 0 || satisfactionRating === 0}
            className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold text-sm flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4 fill-white" />}
            Submit
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Payment Modal ─────────────────────────────────────────────────────────────
const PaymentModal = ({ order, token, onClose }) => {
  const [loading, setLoading] = useState(false);

  const handleEasebuzz = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payment/initiate`,
        { order_id: order.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate payment');
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pay for Order
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="bg-slate-50 rounded-2xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-600">Amount Due</span>
            <span className="text-xl font-black text-slate-900">₹{order.total_amount?.toFixed(2)}</span>
          </div>
          <button
            onClick={handleEasebuzz}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3 rounded-2xl transition-all hover:shadow-lg hover:shadow-blue-200"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Pay via Easebuzz
          </button>
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
            <IndianRupee className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-800">Pay with Cash</p>
              <p className="text-xs text-amber-700 mt-0.5">Hand cash to the delivery boy at pickup/delivery or ask admin to mark it as paid.</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Invoice Modal ─────────────────────────────────────────────────────────────
const InvoiceModal = ({ order, onClose }) => {
  const invoiceNum = order.invoice_number || `JF-INV-${order.id.slice(0, 8).toUpperCase()}`;
  const isPaid = order.payment_status === 'paid';
  const [downloading, setDownloading] = useState(false);
  const token = localStorage.getItem('individualToken');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const gstTotal = order.gst_total || 0;
  const cgstTotal = order.cgst_total || 0;
  const sgstTotal = order.sgst_total || 0;
  const gstTaxableBase = order.gst_taxable_base || 0;

  const handleDownloadPDF = async () => {
    if (!isPaid) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/individual/orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNum}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="flex items-center gap-2"><Receipt className="w-5 h-5 text-blue-600" /> Tax Invoice</span>
            {isPaid ? (
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 rounded-xl px-3 py-1.5 hover:bg-blue-50 disabled:opacity-60"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <IndianRupee className="w-3.5 h-3.5" />}
                {downloading ? 'Downloading…' : 'Download PDF'}
              </button>
            ) : (
              <span className="text-xs text-slate-400 border border-slate-200 rounded-xl px-3 py-1.5">Pay to download</span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl px-5 py-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-black text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>JustFold</p>
                <p className="text-blue-200 text-xs mt-0.5">Patel Nagar, Gurugram, Haryana 122001</p>
              </div>
              <span className="text-[10px] font-black text-blue-300 uppercase tracking-widest border border-blue-400 rounded-lg px-2 py-1">TAX INVOICE</span>
            </div>
            <div className="flex items-center justify-between mt-3">
              <div>
                <p className="text-[10px] text-blue-300 uppercase tracking-widest">Invoice No.</p>
                <p className="font-bold text-sm font-mono">{invoiceNum}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-blue-300 uppercase tracking-widest">Date</p>
                <p className="font-bold text-sm">{safeFormat(order.order_date, 'dd MMM yyyy')}</p>
              </div>
            </div>
          </div>

          {/* Billed To */}
          <div className="bg-slate-50 rounded-2xl px-4 py-3">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Billed To</p>
            <p className="font-bold text-slate-800 text-sm">{order.customer_name}</p>
            {order.customer_phone && <p className="text-xs text-slate-500 mt-0.5">+91 {order.customer_phone}</p>}
            {order.address_line1 && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{[order.address_line2, order.address_line1, order.city, order.pincode].filter(Boolean).join(', ')}</p>}
          </div>

          {/* Items */}
          {order.cart_items?.length > 0 && (
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_auto_auto] text-[9px] font-black text-slate-400 uppercase tracking-widest px-3 py-2 border-b border-slate-100 bg-slate-50">
                <span>Item</span>
                <span className="text-right pr-3">Qty</span>
                <span className="text-right pr-3">Taxable</span>
                <span className="text-right">Total</span>
              </div>
              {order.cart_items.map((item, i) => {
                const taxable = item.total || 0;
                const totalWithGst = item.total_with_gst != null ? item.total_with_gst : taxable;
                const hasGst = item.gst_applicable;
                return (
                  <div key={i} className="px-3 py-2.5 border-b border-slate-50 last:border-0 text-sm">
                    <div className="grid grid-cols-[1fr_auto_auto_auto] items-start gap-1">
                      <div>
                        <p className="font-medium text-slate-800 text-xs leading-snug">{item.description}</p>
                        {hasGst && (
                          <span className="inline-block text-[9px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1 mt-0.5">GST 18%</span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 text-right pr-3 pt-0.5">
                        {item.quantity > 0 ? `${item.quantity} ${item.unit}` : '—'}
                      </p>
                      <p className="text-[10px] text-slate-500 text-right pr-3 pt-0.5">₹{taxable.toFixed(2)}</p>
                      <p className="text-xs font-semibold text-slate-700 text-right pt-0.5">₹{totalWithGst.toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* GST Summary */}
          {gstTotal > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
              <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">GST Summary</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between text-amber-700">
                  <span>Taxable Amount</span>
                  <span>₹{gstTaxableBase.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-amber-700">
                  <span>CGST (9%)</span>
                  <span>₹{cgstTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-amber-700">
                  <span>SGST (9%)</span>
                  <span>₹{sgstTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-amber-800 border-t border-amber-200 pt-1 mt-1">
                  <span>Total GST</span>
                  <span>₹{gstTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="bg-slate-50 rounded-2xl px-4 py-3 space-y-2 text-sm">
            {order.subtotal != null && (
              <div className="flex justify-between text-slate-600">
                <span>Subtotal (pre-tax)</span>
                <span>₹{order.subtotal?.toFixed(2)}</span>
              </div>
            )}
            {order.coupon_discount > 0 && (
              <div className="flex justify-between text-emerald-600">
                <span>Coupon ({order.coupon_code})</span>
                <span className="font-semibold">−₹{order.coupon_discount?.toFixed(2)}</span>
              </div>
            )}
            {order.referral_discount > 0 && (
              <div className="flex justify-between text-violet-600">
                <span>{order.referral_type === 'referrer_reward' ? 'Referral Reward (5%)' : 'Welcome Discount (10%)'}</span>
                <span className="font-semibold">−₹{order.referral_discount?.toFixed(2)}</span>
              </div>
            )}
            {gstTotal > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>GST (CGST + SGST)</span>
                <span>₹{gstTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-slate-600">
              <span>Delivery</span>
              <span>{(order.delivery_charge || 0) > 0 ? `₹${order.delivery_charge?.toFixed(2)}` : 'Free'}</span>
            </div>
            {order.overall_discount_amount > 0 && (
              <>
                <div className="flex justify-between text-slate-500 pt-2 border-t border-slate-200">
                  <span>Subtotal (before discount)</span>
                  <span>₹{(order.total_amount + order.overall_discount_amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-orange-600">
                  <span>Overall Discount ({order.overall_discount_pct?.toFixed(0)}%)</span>
                  <span className="font-semibold">−₹{order.overall_discount_amount.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between pt-2 border-t border-slate-200 font-black text-slate-900 text-base">
              <span>Grand Total</span>
              <span>₹{order.total_amount?.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Status */}
          <div className={`rounded-2xl px-4 py-3 flex items-center gap-3 ${isPaid ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
            {isPaid ? (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-black text-emerald-800">Paid</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    via {order.payment_method === 'easebuzz' ? 'Easebuzz' : 'Cash'}
                    {order.paid_at && ` · ${safeFormat(order.paid_at, 'dd MMM yyyy, h:mm a')}`}
                  </p>
                  {order.payment_txn_id && <p className="text-[10px] text-emerald-500 font-mono mt-0.5">Txn: {order.payment_txn_id}</p>}
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-black text-red-700">Payment Pending</p>
                  <p className="text-xs text-red-500 mt-0.5">Complete payment to download your tax invoice</p>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_CONFIG = {
  pending:          { label: 'Order Placed',        bg: 'bg-amber-50   text-amber-700   border-amber-200',   icon: Clock },
  pickup_assigned:  { label: 'Pickup Scheduled',    bg: 'bg-blue-50    text-blue-700    border-blue-200',    icon: Truck },
  pickup_done:      { label: 'Picked Up',           bg: 'bg-indigo-50  text-indigo-700  border-indigo-200',  icon: Package },
  in_process:       { label: 'Inspection In Progress', bg: 'bg-violet-50  text-violet-700  border-violet-200',  icon: Cog },
  process_complete: { label: 'Inspection Complete',   bg: 'bg-teal-50    text-teal-700    border-teal-200',    icon: CheckCircle },
  cleaned:          { label: 'Cleaned',             bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Sparkles },
  out_for_delivery: { label: 'Out for Delivery',    bg: 'bg-orange-50  text-orange-700  border-orange-200',  icon: Truck },
  delivered:        { label: 'Delivered',           bg: 'bg-green-50   text-green-700   border-green-200',   icon: CheckCircle },
  delivery_overdue: { label: 'Overdue',             bg: 'bg-red-50     text-red-700     border-red-200',     icon: AlertTriangle },
  cancelled:        { label: 'Cancelled',           bg: 'bg-red-50     text-red-700     border-red-200',     icon: XCircle },
};

const STATUS_ORDER = ['pending', 'pickup_assigned', 'pickup_done', 'in_process', 'process_complete', 'cleaned', 'out_for_delivery', 'delivered'];

const IndividualOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail]       = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [issueType, setIssueType]         = useState('');
  const [issueDesc, setIssueDesc]         = useState('');
  const [submitting, setSubmitting]       = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [ratingOrder, setRatingOrder]     = useState(null);
  const [ratedOrderIds, setRatedOrderIds] = useState(new Set());
  const [paymentOrder, setPaymentOrder]   = useState(null);
  const [invoiceOrder, setInvoiceOrder]   = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const dismissedRatings = useRef(new Set());

  const token = localStorage.getItem('individualToken');
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (!token) { navigate('/individual/login'); return; }
    // Check Easebuzz callback result
    const params = new URLSearchParams(location.search);
    const payResult = params.get('payment');
    if (payResult === 'success') {
      toast.success('Payment successful! Your order is now marked as paid.');
      navigate('/individual/orders', { replace: true });
    } else if (payResult === 'failed') {
      toast.error('Payment failed or was cancelled. Please try again.');
      navigate('/individual/orders', { replace: true });
    }
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API}/individual/orders`, { headers: { Authorization: `Bearer ${token}` } });
      const loaded = r.data;
      setOrders(loaded);
      // Check which delivered orders already have ratings
      const delivered = loaded.filter(o => o.status === 'delivered');
      const ratedSet = new Set();
      await Promise.all(delivered.map(async (o) => {
        try {
          const rr = await axios.get(`${API_BASE}/individual/orders/${o.id}/rating`, { headers: { Authorization: `Bearer ${token}` } });
          if (rr.data && rr.data.id) ratedSet.add(o.id);
        } catch { /* ignore */ }
      }));
      setRatedOrderIds(ratedSet);
      // Auto-popup: only most recent unrated, delivered within 48h, not already dismissed
      const cutoff = Date.now() - 48 * 60 * 60 * 1000;
      const unrated = delivered.find(o =>
        !ratedSet.has(o.id) &&
        !dismissedRatings.current.has(o.id) &&
        o.delivery_date && new Date(o.delivery_date).getTime() > cutoff
      );
      if (unrated) setRatingOrder(unrated);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/individual/login'); return; }
      toast.error('Failed to load orders');
    } finally { setLoading(false); }
  };

  const isOverdue = (o) => {
    if (o.status === 'delivered') return false;
    const exp = o.expected_delivery_date;
    return exp ? new Date(exp) < new Date() : false;
  };

  const effectiveStatus = (o) => isOverdue(o) ? 'delivery_overdue' : o.status;

  const handleRaiseIssue = async () => {
    if (!issueType) { toast.error('Please select an issue type'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/individual/orders/${selectedOrder.id}/issue`,
        { issue_type: issueType, description: issueDesc },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Issue raised successfully');
      setShowIssueModal(false); setIssueType(''); setIssueDesc('');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to raise issue'); }
    finally { setSubmitting(false); }
  };

  const handleCancelOrder = async () => {
    if (!cancellingOrder) return;
    setCancelSubmitting(true);
    try {
      await axios.post(`${API}/individual/orders/${cancellingOrder.id}/cancel`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order cancelled');
      setShowCancelConfirm(false);
      setCancellingOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot cancel this order');
    } finally {
      setCancelSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/individual/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>My Orders</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Page header */}
            <div className="hidden md:flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Orders</h1>
                <p className="text-sm text-slate-500 mt-0.5">{orders.length} order{orders.length !== 1 ? 's' : ''} total</p>
              </div>
              <button onClick={() => navigate('/individual/new-order')}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-orange-200 hover:-translate-y-0.5 text-sm">
                <Plus className="w-4 h-4" /> New Order
              </button>
            </div>

            {orders.length === 0 ? (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] py-20 px-8 flex flex-col items-center text-center shadow-sm space-y-5">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-300">
                  <Package className="w-12 h-12" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-600 mb-1">No orders yet</p>
                  <p className="text-sm text-slate-400">Place your first order and we'll take care of the rest</p>
                </div>
                <button onClick={() => navigate('/individual/new-order')}
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-2xl shadow-md shadow-orange-200 transition-all hover:-translate-y-0.5">
                  <Plus className="w-5 h-5" /> Place Your First Order
                </button>
              </section>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => {
                  const overdue = isOverdue(order);
                  const es = effectiveStatus(order);
                  const cfg = STATUS_CONFIG[es] || STATUS_CONFIG.pending;
                  const Icon = cfg.icon;
                  // Only show delivery date once admin has processed (after pickup + photos)
                  const expDel = ['in_process', 'process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(order.status) ? order.expected_delivery_date : null;
                  const activeIdx = STATUS_ORDER.indexOf(order.status);

                  return (
                    <button key={order.id}
                      onClick={() => { setSelectedOrder(order); setShowDetail(true); }}
                      className={`w-full text-left bg-white border rounded-[1.5rem] p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 ${overdue ? 'border-red-200 bg-red-50/20' : 'border-slate-200/80'}`}>
                      {/* Top row */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${overdue ? 'bg-red-50' : 'bg-orange-50'}`}>
                            <Icon className={`w-5 h-5 ${overdue ? 'text-red-500' : 'text-orange-500'}`} />
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{order.invoice_number || `Laundry Order`}</p>
                            {order.clothes_type && <p className="text-xs text-slate-500 mt-0.5">{order.clothes_type}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {order.is_split && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-600">Split</span>
                          )}
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${cfg.bg}`}>{cfg.label}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-slate-50 rounded-xl px-3 py-2">
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Order Date</p>
                          <p className="text-sm font-semibold text-slate-700">{safeFormat(order.order_date, 'dd MMM yyyy')}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl px-3 py-2">
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Pickup Slot</p>
                          <p className="text-sm font-semibold text-slate-700">
                            {order.pickup_date ? safeFormat(order.pickup_date, 'dd MMM') : '—'}
                            {order.pickup_time_slot ? <span className="text-xs text-slate-500 block">{order.pickup_time_slot}</span> : null}
                          </p>
                        </div>
                      </div>

                      {/* Progress bar — hidden for cancelled orders */}
                      {order.status === 'cancelled' ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-red-700">Order Cancelled</p>
                            <p className="text-[10px] text-red-500">
                              {order.cancelled_by === 'customer' ? 'Cancelled by you' : 'Cancelled by JustFold'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <div className="relative flex items-center">
                            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-slate-100" />
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-blue-500 transition-all"
                              style={{ width: activeIdx <= 0 ? '0%' : `${(activeIdx / (STATUS_ORDER.length - 1)) * 100}%` }} />
                            <div className="relative flex justify-between w-full">
                              {STATUS_ORDER.map((s, i) => {
                                const done = i <= activeIdx;
                                const current = i === activeIdx;
                                return (
                                  <div key={s} className={`w-5 h-5 rounded-full flex items-center justify-center z-10 text-[9px] font-bold flex-shrink-0 transition-all ${
                                    current ? 'bg-blue-600 text-white ring-2 ring-blue-200 scale-125' :
                                    done ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'
                                  }`}>{i + 1}</div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-slate-400">Step {activeIdx + 1}/{STATUS_ORDER.length}</span>
                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                              {STATUS_CONFIG[order.status]?.label || order.status}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Badges */}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {/* Payment status — only show after inspection complete */}
                        {order.total_amount > 0 && order.payment_status === 'paid' && (
                          <span className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">
                            <CheckCircle className="w-3 h-3" /> Paid {order.payment_method === 'cash' ? '(Cash)' : '(Easebuzz)'}
                          </span>
                        )}
                        {order.total_amount > 0 && order.payment_status !== 'paid' && ['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(order.status) && (
                          <span className="flex items-center gap-1 text-[10px] text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-semibold">
                            <XCircle className="w-3 h-3" /> Unpaid
                          </span>
                        )}
                        {order.photos?.length > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">
                            <Camera className="w-3 h-3" /> {order.photos.length} photo{order.photos.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {order.issue && (
                          <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold border ${order.issue.status === 'open' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                            <AlertTriangle className="w-3 h-3" /> Issue: {order.issue.status}
                          </span>
                        )}
                        {order.status === 'delivered' && !ratedOrderIds.has(order.id) && (
                          <button
                            onClick={e => { e.stopPropagation(); setRatingOrder(order); }}
                            className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-bold hover:bg-amber-100 transition-colors"
                          >
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Rate Order
                          </button>
                        )}
                        {ratedOrderIds.has(order.id) && (
                          <span className="flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">
                            <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" /> Rated
                          </span>
                        )}
                      </div>
                      {/* Cancel button — only before pickup */}
                      {['pending', 'pickup_assigned'].includes(order.status) && (
                        <div className="mt-3">
                          <button
                            onClick={e => { e.stopPropagation(); setCancellingOrder(order); setShowCancelConfirm(true); }}
                            className="flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 px-3 py-1.5 rounded-xl transition-colors">
                            <XCircle className="w-3.5 h-3.5" /> Cancel Order
                          </button>
                        </div>
                      )}

                      {/* Action buttons row */}
                      {order.total_amount > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          {order.payment_status !== 'paid' && ['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(order.status) && (
                            <button
                              onClick={e => { e.stopPropagation(); setPaymentOrder(order); }}
                              className="flex items-center gap-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-xl transition-colors"
                            >
                              <CreditCard className="w-3.5 h-3.5" /> Pay Now
                            </button>
                          )}
                          {['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(order.status) && (
                            <button
                              onClick={e => { e.stopPropagation(); setInvoiceOrder(order); }}
                              className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-xl transition-colors"
                            >
                              <Receipt className="w-3.5 h-3.5" /> View Invoice
                            </button>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Order Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Package className="w-5 h-5 text-orange-500" /> Order Details
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (() => {
            const overdue = isOverdue(selectedOrder);
            const es = effectiveStatus(selectedOrder);
            const cfg = STATUS_CONFIG[es] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            // Only show delivery date once admin has processed the order
            const expDel = ['in_process', 'process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status) ? selectedOrder.expected_delivery_date : null;
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border ${cfg.bg}`}>
                    <StatusIcon className="w-3.5 h-3.5" />{cfg.label}
                  </span>
                  <div className="flex items-center gap-2">
                    {selectedOrder.total_amount > 0 && (
                      selectedOrder.payment_status === 'paid' ? (
                        <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="w-3.5 h-3.5" /> Paid
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border bg-red-50 text-red-600 border-red-200">
                          <XCircle className="w-3.5 h-3.5" /> Unpaid
                        </span>
                      )
                    )}
                    <span className="text-sm text-slate-500 font-medium">{selectedOrder.total_clothes} items</span>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-4 space-y-2.5 text-sm">
                  {selectedOrder.clothes_type && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Type</span>
                      <span className="text-slate-800 font-medium">{selectedOrder.clothes_type}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">Order Date</span>
                    <span className="text-slate-800">{safeFormat(selectedOrder.order_date, 'dd MMM yyyy')}</span>
                  </div>
                  {selectedOrder.pickup_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Scheduled Pickup</span>
                      <span className="text-slate-800">
                        {selectedOrder.pickup_date}
                        {selectedOrder.pickup_time_slot && ` @ ${selectedOrder.pickup_time_slot}`}
                      </span>
                    </div>
                  )}
                  {expDel ? (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Expected Delivery</span>
                      <span className={overdue ? 'text-red-600 font-semibold' : 'text-orange-700 font-medium'}>
                        {safeFormat(expDel, 'dd MMM yyyy, h:mm a')}
                      </span>
                    </div>
                  ) : !['delivered'].includes(selectedOrder.status) && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Expected Delivery</span>
                      <span className="text-slate-400 italic text-xs">Will be updated after pickup</span>
                    </div>
                  )}
                  {selectedOrder.delivery_date && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Delivered On</span>
                      <span className="text-emerald-700 font-medium">{safeFormat(selectedOrder.delivery_date, 'dd MMM yyyy')}</span>
                    </div>
                  )}
                  {selectedOrder.special_instructions && (
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-slate-400 text-xs mb-1">Special Instructions</p>
                      <p className="text-slate-700">{selectedOrder.special_instructions}</p>
                    </div>
                  )}
                </div>

                {/* Pricing Breakdown (delivery charge + coupon) — only after inspection */}
                {['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status) && (selectedOrder.delivery_charge > 0 || selectedOrder.coupon_discount > 0) && (
                  <div className="bg-slate-50 rounded-2xl p-4 space-y-2 text-sm border border-slate-100">
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Pricing Breakdown</p>
                    {selectedOrder.subtotal != null && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Subtotal</span>
                        <span className="text-slate-700 font-medium">₹{selectedOrder.subtotal?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.coupon_discount > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Coupon ({selectedOrder.coupon_code})</span>
                        <span className="font-bold">−₹{selectedOrder.coupon_discount?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.referral_discount > 0 && (
                      <div className="flex justify-between text-violet-600">
                        <span>{selectedOrder.referral_type === 'referrer_reward' ? 'Referral Reward (5%)' : 'Welcome Discount (10%)'}</span>
                        <span className="font-bold">−₹{selectedOrder.referral_discount?.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.delivery_charge > 0 ? (
                      <div className="flex justify-between text-orange-600">
                        <span>Delivery Charge</span>
                        <span className="font-bold">+₹{selectedOrder.delivery_charge?.toFixed(2)}</span>
                      </div>
                    ) : selectedOrder.delivery_distance_km != null && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Delivery ({selectedOrder.delivery_distance_km} km)</span>
                        <span className="font-bold">Free</span>
                      </div>
                    )}
                    {selectedOrder.delivery_charge_reason && (
                      <p className="text-[10px] text-slate-400 italic">{selectedOrder.delivery_charge_reason}</p>
                    )}
                    {['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status) && (
                      <div className="flex justify-between pt-2 border-t border-slate-200 font-black text-slate-800">
                        <span>Total</span>
                        <span>₹{selectedOrder.total_amount?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Cart Items Breakdown */}
                {selectedOrder.cart_items?.length > 0 && (() => {
                  const priceVisible = ['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  return (
                    <div className="bg-white border border-slate-200 rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Tag className="w-3.5 h-3.5 text-slate-600" />
                          </div>
                          <span className="text-sm font-bold text-slate-700">Order Items</span>
                        </div>
                        {!priceVisible && (
                          <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                            Pricing after inspection
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {selectedOrder.cart_items.map((item, i) => {
                          const hasDiscount = (item.discount_pct || 0) > 0;
                          const finalPrice = item.final_total != null ? item.final_total : item.total;
                          return (
                            <div key={i} className={`flex items-center justify-between text-xs py-1.5 border-b border-slate-100 last:border-0 ${item.pending_breakdown ? 'opacity-80' : ''}`}>
                              <div className="flex-1 mr-2">
                                <p className="text-slate-700 font-medium">{item.description}</p>
                                {item.total_pcs > 0 && item.pending_breakdown && (
                                  <p className="text-amber-600 font-semibold">{item.total_pcs} pcs picked up</p>
                                )}
                                {item.quantity > 0 && !item.pending_breakdown && (
                                  <p className="text-slate-400">
                                    {item.quantity} {item.unit}
                                    {priceVisible && ` × ₹${item.price_per_unit}`}
                                  </p>
                                )}
                              </div>
                              {item.pending_breakdown ? (
                                <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                  Being assessed
                                </span>
                              ) : priceVisible ? (
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {hasDiscount ? (
                                    <>
                                      <span className="line-through text-slate-400">₹{item.total}</span>
                                      <span className="bg-orange-100 text-orange-600 font-bold px-1.5 py-0.5 rounded-md text-[10px]">-{item.discount_pct}%</span>
                                      <span className="text-emerald-600 font-bold text-sm">₹{finalPrice}</span>
                                    </>
                                  ) : (
                                    <span className="text-slate-700 font-semibold">₹{item.total}</span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                        {priceVisible && (
                          <div className="pt-2 flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-500">
                              {selectedOrder.cart_items.some(i => (i.discount_pct || 0) > 0) ? 'Total (after discounts)' : 'Total'}
                            </span>
                            <span className="text-sm font-black text-slate-800">
                              ₹{selectedOrder.cart_items.reduce((s, item) =>
                                s + (item.final_total != null ? item.final_total : item.total), 0
                              ).toLocaleString('en-IN')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Service Timelines */}
                {selectedOrder.service_timelines?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock3 className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-700">Delivery Timelines</span>
                    </div>
                    <div className="space-y-2">
                      {selectedOrder.service_timelines.map((tl) => {
                        const isPast = new Date(tl.due_date) < new Date();
                        const isDelivered = selectedOrder.status === 'delivered';
                        return (
                          <div key={tl.service} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isDelivered ? 'bg-emerald-500' : isPast ? 'bg-red-400' : 'bg-blue-400'}`} />
                              <span className="text-slate-700 font-medium">{tl.label}</span>
                            </div>
                            <div className="text-right">
                              <span className={`font-semibold ${isDelivered ? 'text-emerald-600' : isPast ? 'text-red-500' : 'text-blue-600'}`}>
                                {tl.days} {tl.days === 1 ? 'day' : 'days'}
                              </span>
                              <span className="text-slate-400 ml-1">by {safeFormat(tl.due_date, 'dd MMM, h:mm a')}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {selectedOrder.service_timelines.some(tl => tl.custom_note) && (
                      <div className="mt-3 pt-2 border-t border-blue-100 space-y-1">
                        {selectedOrder.service_timelines.filter(tl => tl.custom_note).map(tl => (
                          <p key={tl.service} className="text-[11px] text-slate-500 italic">{tl.label}: {tl.custom_note}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Split Order Sub-orders */}
                {selectedOrder?.is_split && selectedOrder?.sub_orders?.length > 0 && (
                  <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package className="w-3.5 h-3.5 text-orange-600" />
                      </div>
                      <span className="text-sm font-bold text-slate-700">Your order is split into 2 parts</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">Express services are delivered separately before standard services.</p>
                    <div className="space-y-3">
                      {selectedOrder.sub_orders.map((sub, idx) => (
                        <div key={idx} className={`rounded-xl p-3 border ${sub.type === 'express' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`text-xs font-bold ${sub.type === 'express' ? 'text-amber-700' : 'text-blue-700'}`}>{sub.label}</span>
                            {['processed', 'delivered'].includes(sub.status) && sub.expected_delivery_date ? (
                              <span className="text-xs text-slate-500">by {safeFormat(sub.expected_delivery_date, 'dd MMM, h:mm a')}</span>
                            ) : (
                              <span className="text-xs text-slate-400 italic">Delivery TBD</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {sub.services.map((s, i) => (
                              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${sub.type === 'express' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{s}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photos */}
                {selectedOrder.photos?.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
                      <Camera className="w-4 h-4 text-blue-600" />
                      Verification Photos ({selectedOrder.photos.length})
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedOrder.photos.map((photo, idx) => (
                        <button key={idx}
                          onClick={() => { setSelectedPhoto(photo); setShowPhotoModal(true); }}
                          className="relative group rounded-xl overflow-hidden border border-slate-200">
                          <img src={`${BACKEND_URL}${photo.url}`} alt="Order verification"
                            className="w-full h-20 object-cover group-hover:opacity-80 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Issue */}
                {selectedOrder.issue ? (
                  <div className={`p-4 rounded-2xl border ${selectedOrder.issue.status === 'open' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className={`w-4 h-4 ${selectedOrder.issue.status === 'open' ? 'text-amber-600' : 'text-emerald-600'}`} />
                      <span className={`text-sm font-bold ${selectedOrder.issue.status === 'open' ? 'text-amber-800' : 'text-emerald-800'}`}>
                        {selectedOrder.issue.issue_type}
                      </span>
                      <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${selectedOrder.issue.status === 'open' ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`}>
                        {selectedOrder.issue.status}
                      </span>
                    </div>
                    {selectedOrder.issue.description && <p className="text-xs text-slate-600 mt-1">{selectedOrder.issue.description}</p>}
                    {selectedOrder.issue.resolution && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-400">Resolution:</p>
                        <p className="text-xs text-slate-700 font-medium mt-0.5">{selectedOrder.issue.resolution}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  selectedOrder.photos?.length > 0 && selectedOrder.status !== 'delivered' && (
                    <button
                      onClick={() => { setShowDetail(false); setShowIssueModal(true); }}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-amber-200 text-amber-600 hover:bg-amber-50 font-semibold text-sm transition-all">
                      <MessageSquare className="w-4 h-4" /> Raise an Issue
                    </button>
                  )
                )}

                {/* Payment / Invoice actions */}
                {selectedOrder.total_amount > 0 && (
                  <div className="flex gap-2 pt-1">
                    {selectedOrder.payment_status !== 'paid' && ['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status) && (
                      <button
                        onClick={() => { setShowDetail(false); setPaymentOrder(selectedOrder); }}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-2xl text-sm transition-all hover:shadow-lg hover:shadow-blue-200"
                      >
                        <CreditCard className="w-4 h-4" /> Pay Now
                      </button>
                    )}
                    {selectedOrder.payment_status !== 'paid' && !['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status) && (
                      <div className="flex-1 flex items-center justify-center gap-2 bg-slate-100 text-slate-400 font-semibold py-3 rounded-2xl text-sm cursor-not-allowed">
                        <CreditCard className="w-4 h-4" /> Pay after Inspection
                      </div>
                    )}
                    {['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(selectedOrder.status) && (
                      <button
                        onClick={() => { setShowDetail(false); setInvoiceOrder(selectedOrder); }}
                        className="flex-1 flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-semibold py-3 rounded-2xl text-sm hover:bg-slate-50 transition-all"
                      >
                        <Receipt className="w-4 h-4" /> View Invoice
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Photo viewer */}
      <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
        <DialogContent className="max-w-lg p-2 rounded-3xl">
          {selectedPhoto && (
            <img src={`${BACKEND_URL}${selectedPhoto.url}`} alt="Order verification" className="w-full rounded-2xl" />
          )}
        </DialogContent>
      </Dialog>

      {/* Raise issue modal */}
      <Dialog open={showIssueModal} onOpenChange={setShowIssueModal}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Raise an Issue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Issue Type</label>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger className="rounded-xl border-slate-200 bg-slate-50">
                  <SelectValue placeholder="Select issue type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mismatch">Clothes Mismatch</SelectItem>
                  <SelectItem value="Damaged">Clothes Damaged</SelectItem>
                  <SelectItem value="Missing Items">Missing Items</SelectItem>
                  <SelectItem value="Wrong Items">Wrong Items Received</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Description (Optional)</label>
              <Textarea value={issueDesc} onChange={e => setIssueDesc(e.target.value)}
                placeholder="Describe the issue..." className="min-h-[80px] rounded-xl border-slate-200 bg-slate-50" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowIssueModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-all">
              Cancel
            </button>
            <button onClick={handleRaiseIssue} disabled={submitting || !issueType}
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold text-sm transition-all flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Issue'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Modal */}
      {ratingOrder && (
        <RatingModal
          order={ratingOrder}
          token={token}
          onClose={() => { dismissedRatings.current.add(ratingOrder.id); setRatingOrder(null); }}
          onDone={(id) => {
            setRatedOrderIds(prev => new Set([...prev, id]));
            setRatingOrder(null);
          }}
        />
      )}

      {/* Payment Modal */}
      {paymentOrder && (
        <PaymentModal
          order={paymentOrder}
          token={token}
          onClose={() => { setPaymentOrder(null); fetchOrders(); }}
        />
      )}

      {/* Invoice Modal */}
      {invoiceOrder && (
        <InvoiceModal
          order={invoiceOrder}
          onClose={() => setInvoiceOrder(null)}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-black text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <XCircle className="w-5 h-5" /> Cancel Order?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-600">
              Are you sure you want to cancel this order? This cannot be undone.
            </p>
            <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-500">
              You can only cancel before pickup. Once our team has picked up your clothes, cancellation is not possible.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => { setShowCancelConfirm(false); setCancellingOrder(null); }}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
              Keep Order
            </button>
            <button onClick={handleCancelOrder} disabled={cancelSubmitting}
              className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-1.5">
              {cancelSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Yes, Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IndividualBottomNav />
    </div>
  );
};

export default IndividualOrders;
